import {Button, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, TextField} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PropTypes from 'prop-types';
import React, {useRef, useEffect, useState} from 'react';
import {baseNationalityOptions, otherNationalityOptions} from '../../utils/Nationalities.js';

const MAX_OTHERS = 3;

const useStyles = makeStyles(() => ({
  question: {
    '&&': {
      marginBottom: 15,
      paddingTop: 15,
      fontWeight: 'bold',
      fontSize: 16
    }
  },
  fieldset: {
    '&&': {
      width: '100%',
      maxWidth: 'inherit'
    }
  },
  otherFieldset: {
    border: 'none',
    marginLeft: 10,
    padding: 0,
    borderLeft: '3px solid #ccc',
  },
  otherList: {
    listStyle: 'none',
    margin: 0,
    paddingLeft: 16,
  },
  otherItem: {
    marginBottom: 12
  },
  otherRow: {
    display: 'flex',
    alignItems: 'center',
  },
  removeBtn: {
    marginLeft: 12
  },
  addBtn: {
    marginTop: 8,
    marginLeft: 16
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    width: 1
  },
}));

const ordinalLabels = ['First', 'Second', 'Third'];

const NationalitySelector = (props) => {
  const {label, nationalities = [], otherNationalities = [], handleChange, showError, errorMsg} = props;
  const classes = useStyles();
  const [focusIndex, setFocusIndex] = useState(null);
  const [focusList, setFocusList] = useState(false);
  const listRef = useRef(null);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (focusIndex !== null) {
      inputRefs.current[focusIndex]?.focus();
      setFocusIndex(null);
    }

    if (focusList) {
      listRef.current?.focus();
      setFocusList(false);
    }
  }, [otherNationalities, focusIndex, focusList]);

  const updateParent = (updatedNationalities, updatedOthers) => {
    handleChange({
      nationalities: updatedNationalities,
      otherNationalities: updatedOthers
    });
  };

  const handleCheckboxChange = (event) => {
    const {value: option, checked} = event.target;

    let updatedNationalities = [...nationalities];

    if (checked) {
      updatedNationalities.push(option);
    } else {
      updatedNationalities = updatedNationalities.filter(n => n !== option);
    }

    let updatedOthers = [...otherNationalities];

    if (option === 'other') {
      updatedOthers = checked ? [''] : [];
    }

    updateParent(updatedNationalities, updatedOthers);
  };

  const handleOtherChange = (index, newValue) => {
    const updated = [...otherNationalities];
    updated[index] = newValue || '';
    updateParent(nationalities, updated);
  };

  const addOtherNationality = () => {
    if (otherNationalities.length >= MAX_OTHERS) return;

    const next = [...otherNationalities, ''];

    updateParent(nationalities, next);

    setFocusIndex(next.length - 1);
  };

  const removeOtherNationality = (index) => {
    if (otherNationalities.length === 1) return;

    const updated = otherNationalities.filter((o, i) => i !== index);

    updateParent(nationalities, updated);

    setFocusList(true);
  };

  const otherSelected = nationalities.includes('other');

  const canAddAnother =
    otherNationalities.length < MAX_OTHERS &&
    (otherNationalities.length === 0 ||
      otherNationalities[otherNationalities.length - 1] !== '');

  return (
    <FormControl
      component="fieldset"
      className={classes.fieldset}
      aria-describedby={showError ? 'nationality-error' : ''}
      error={showError}
    >

      <FormLabel component="legend" className={classes.question} data-testid="label-nationality">
        {label}
      </FormLabel>

      <div className={showError ? 'inline-error' : ''}>
        {showError &&
          <p id="nationality-error" role="alert">{errorMsg || 'Please select at least one nationality'}</p>
        }

        <FormGroup>
          {baseNationalityOptions.map(option => (
            <FormControlLabel
              key={option.code}
              aria-controls={option.code === 'other' ? 'other-nationalities-list' : ''}
              control={
                <Checkbox
                  color="primary"
                  checked={nationalities.includes(option.code)}
                  onChange={handleCheckboxChange}
                  value={option.code}
                />
              }
              label={option.label}
              data-testid="base-nationality-checkbox"
            />
          ))}
        </FormGroup>

        {otherSelected && (
          <fieldset className={classes.otherFieldset} >

            <legend className={classes.visuallyHidden} id="other-nationalities-label">
              Other nationalities
            </legend>

            <ul
              id="other-nationalities-list"
              aria-labelledby="other-nationalities-label"
              ref={listRef} tabIndex="-1"
              className={classes.otherList}
            >
              {otherNationalities.map((otherNationalityCode, index) => {

                const ordinal = ordinalLabels[index] || `${index + 1}th`;
                const selectedOption = otherNationalityOptions.find(o => o.code === otherNationalityCode) || null;

                return (
                  <li key={ordinal} className={classes.otherItem}>
                    <div className={classes.otherRow}>
                      <Autocomplete
                        options={otherNationalityOptions.filter(
                          option => option.code === otherNationalities[index] || !otherNationalities.includes(option.code)
                        )}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                        value={selectedOption}
                        onChange={(e, newVal) => {
                          const value = typeof newVal === 'string' ? newVal : newVal?.code || '';
                          handleOtherChange(index, value);
                        }}
                        renderOption={(option) => option.label}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label={`${ordinal} other nationality`}
                            inputRef={(el) => (inputRefs.current[index] = el)}
                            inputProps={{
                              ...params.inputProps,
                              autoComplete: 'off'
                            }}
                          />
                        )}
                        style={{width: 320}}
                        autoHighlight
                        disableClearable
                        data-testid={`other-nationality-checkbox-${index}`}
                      />

                      <Button
                        className={`${classes.removeBtn} ${otherNationalities.length === 1 ? 'Mui-disabled' : ''}`}
                        variant="outlined"
                        aria-disabled={otherNationalities.length === 1}
                        onClick={() => {
                          if (otherNationalities.length === 1) return;
                          removeOtherNationality(index);
                        }}
                      >
                        Remove <span className={classes.visuallyHidden}>{`${ordinal.toLowerCase()} other nationality`}</span>
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>

            <Button
              className={`${classes.addBtn} ${!canAddAnother ? 'Mui-disabled' : ''}`}
              variant="outlined"
              aria-disabled={!canAddAnother}
              aria-controls="other-nationalities-list"
              onClick={() => {
                if (!canAddAnother) return;
                addOtherNationality();
              }}
            >
              Add another nationality
            </Button>

          </fieldset>
        )}

      </div>

    </FormControl>
  );
};

NationalitySelector.propTypes = {
  handleChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  nationalities: PropTypes.array,
  otherNationalities: PropTypes.array,
  showError: PropTypes.bool,
  errorMsg: PropTypes.string
};

export default NationalitySelector;
